import { View, StyleSheet } from "react-native";
import { MotiView } from "moti";
import { useAppState } from "../contexts/AppStateContext";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import MainMenuContainer from "../containers/navigation/MainMenuContainer/MainMenuContainer";
import ChatBarContainer from "../containers/navigation/ChatBarContainer/ChatBarContainer";
import LearnMenuContainer from "../containers/navigation/LearnMenuContainer/LearnMenuContainer";
import ContentMenuContainer from "../containers/navigation/ContentMenuContainer/ContentMenuContainer";
import ChatWindowContainer from "../containers/chatWindow/ChatWindowContainer/ChatWindowContainer";
import { useChatBar } from "../contexts/ChatBarContext";
import CustomCourseworkMenuContainer from "../containers/navigation/CustomCourseworkMenuContainer.js/CustomCourseworkMenuContainer";
import { useAppConfig } from "../AppConfigProvider";
import TopMenuContainer from "../containers/navigation/TopMenuContainer/TopMenuContainer";
import { ScrollView } from "react-native-gesture-handler";
import MobileDefaultLayout from "./MobileDefaultLayout";
import CourseHeaderContainer from "../containers/customCoursework/CourseHeaderContainer";
import ComingSoonOverlay from "../components/navigation/ComingSoonOverlay/ComingSoonOverlay";
import PreviewModeBannerContainer from "../containers/adminDashboard/PreviewModeBannerContainer/PreviewModeBannerContainer";
import { usePreviewMode } from "../contexts/PreviewModeContext";
import { useRoute } from "@react-navigation/native";

function DefaultLayout({
  children,
  menuType = "default",
  pageHasChat = true,
  courseName = "",
  showCourseNav = false,
  withPadding = true,
  comingSoon = false,
}) {
  const { state } = useAppState();
  const appConfig = useAppConfig();
  const isAxioOffering = appConfig.isAxioOffering;
  const { isMedium } = useResponsiveScreen();
  const { showChat } = useChatBar();
  const { isPreviewMode } = usePreviewMode();

  const route = useRoute();
  const EDITOR_ROUTES = ["Custom Coursework Modules"];

  const isEditor = EDITOR_ROUTES.includes(route.name);

  // Helper to select sidebar component
  const getSidebar = () => {
    switch (menuType) {
      case "learn":
        return <LearnMenuContainer />;
      case "customCoursework":
        return <CustomCourseworkMenuContainer />;
      case "content":
        return <ContentMenuContainer courseName={courseName} />;
      case "none":
        return null;
      default:
        if (!isAxioOffering) {
          return <MainMenuContainer />;
        }
        return;
    }
  };

  if (!isMedium) {
    return (
      <MobileDefaultLayout
        menuType={menuType}
        courseName={courseName}
        pageHasChat={pageHasChat}
        showCourseNav={showCourseNav}>
        {isPreviewMode && <PreviewModeBannerContainer />}
        {children}
      </MobileDefaultLayout>
    );
  }

  return (
    <View style={{ flex: 1, flexDirection: "column", backgroundColor: "#fff" }}>
      {isPreviewMode && <PreviewModeBannerContainer />}

      {isAxioOffering && (
        <View id="header-container" style={styles.headerContainer}>
          {showCourseNav ? <CourseHeaderContainer isEditor={isEditor} /> : <TopMenuContainer />}
        </View>
      )}
      <View style={{ flex: 1, flexDirection: "row", position: "relative" }}>
        {/* Sidebar if present */}
        {getSidebar() && <View style={styles.sidebarContainer}>{getSidebar()}</View>}
        {/* Main content area */}
        <View style={styles.contentArea}>
          <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={true}>
            <View style={withPadding ? styles.centerWrapperPadding : styles.centerWrapper}>
              <View style={styles.childrenContainer} aria-live="polite">
                {children}
              </View>
            </View>
          </ScrollView>
          {pageHasChat && (
            <MotiView
              style={styles.chatBarContainer}
              animate={{
                scale: state.meta.isExpanded ? 1 : 1,
                opacity: state.meta.isExpanded ? 1 : 1,
              }}
              // @ts-expect-error - transition is not typed
              transition={{ type: "timing", duration: 650 }}>
              <ChatBarContainer />
            </MotiView>
          )}
        </View>
        {comingSoon && <ComingSoonOverlay />}
      </View>
      {showChat && <ChatWindowContainer />}
    </View>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    width: "100%",
    zIndex: 10,
    backgroundColor: "#fff",
  },
  sidebarContainer: {
    minWidth: 220,
    maxWidth: 400,
    backgroundColor: "#fff",
    borderRightWidth: 1,
    borderRightColor: "#eee",
    zIndex: 10,
  },
  contentArea: {
    flex: 1,
    flexDirection: "column",
    position: "relative",
    backgroundColor: "#fff",
  },
  centerWrapperPadding: {
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 40,
    paddingBottom: 0,
  },
  centerWrapper: {},
  childrenContainer: {
    maxWidth: 1340,
    width: "100%",
    marginHorizontal: "auto",
    marginBottom: 100,
  },
  chatBarContainer: {
    width: "100%",
    alignSelf: "center",
    zIndex: 999,
    paddingHorizontal: 40,
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
  },
  scrollView: {
    height: 20,
  },
});

export default DefaultLayout;
